import React from 'react';
import { Menu, X, ChevronDown, Stethoscope, UserRound, Building2, Briefcase, Sparkles, FileText } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../components/ui/dropdown-menu';
import LanguageSelector from '../../components/LanguageSelector';
import Logo from '../../components/Logo';

export const LandingHeader = ({ mobileMenuOpen, navigate, services, setMobileMenuOpen, t }) => (
      <header className="fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-sm border-b border-orange-100 z-50">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
          <div className="flex items-center gap-2 lg:gap-3">
            <Logo size="md" className="hidden sm:flex" />
            <Logo size="sm" className="flex sm:hidden" />
            <div>
              <h1 className="font-heading text-xl lg:text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-orange-600 to-red-600">
                MedRahi
              </h1>
              <p className="text-xs text-gray-600 hidden sm:block">Healthcare Without Borders</p>
            </div>
          </div>
          
          {/* Desktop Navigation - Only show on lg and above */}
          <nav className="hidden lg:flex items-center gap-4 xl:gap-6">
            <button onClick={() => navigate('/treatments')} className="text-sm font-medium text-gray-700 hover:text-orange-600 transition-colors flex items-center gap-1.5">
              <Stethoscope className="w-4 h-4" />
              {t('nav.treatments')}
            </button>
            <button onClick={() => navigate('/doctors')} className="text-sm font-medium text-gray-700 hover:text-orange-600 transition-colors flex items-center gap-1.5">
              <UserRound className="w-4 h-4" />
              {t('nav.doctors')}
            </button>
            <button onClick={() => navigate('/hospitals')} className="text-sm font-medium text-gray-700 hover:text-orange-600 transition-colors flex items-center gap-1.5">
              <Building2 className="w-4 h-4" />
              {t('nav.hospitals')}
            </button>
            
            {/* Services Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="text-sm font-medium text-gray-700 hover:text-orange-600 transition-colors flex items-center gap-1.5" data-testid="services-dropdown-trigger">
                  <Briefcase className="w-4 h-4" />
                  {t('nav.services')}
                  <ChevronDown className="w-3.5 h-3.5" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="center" className="w-56">
                {services.map((service) => {
                  const ServiceIcon = service.icon;
                  return (
                    <DropdownMenuItem 
                      key={service.path} 
                      onClick={() => navigate(service.path)}
                      className="cursor-pointer hover:bg-orange-50 flex items-center gap-2"
                      data-testid={`service-link-${service.path.split('/').pop()}`}
                    >
                      <ServiceIcon className="w-4 h-4 text-orange-500" />
                      {service.name}
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>
            
            <button onClick={() => navigate('/wellness')} className="text-sm font-medium text-gray-700 hover:text-orange-600 transition-colors flex items-center gap-1.5">
              <Sparkles className="w-4 h-4" />
              {t('nav.wellness')}
            </button>
            <button onClick={() => navigate('/medical-visa')} className="text-sm font-medium text-gray-700 hover:text-orange-600 transition-colors flex items-center gap-1.5">
              <FileText className="w-4 h-4" />
              {t('nav.medicalVisa')}
            </button>
          </nav>

          {/* Desktop Auth Buttons - Only show on lg and above */}
          <div className="hidden lg:flex items-center gap-2 xl:gap-3">
            <LanguageSelector />
            <button
              onClick={() => navigate('/login')}
              className="px-3 xl:px-4 py-2 text-sm font-medium text-gray-700 hover:text-orange-600 transition-colors"
            >
              {t('nav.signIn')}
            </button>
            <button
              onClick={() => navigate('/register')}
              className="px-4 xl:px-6 py-2.5 bg-gradient-to-r from-orange-500 to-red-600 text-white rounded-lg font-semibold hover:shadow-lg transition-all text-sm"
            >
              {t('nav.getStarted')}
            </button>
          </div>
          
          {/* Tablet/Mobile: Show key actions + hamburger */}
          <div className="flex lg:hidden items-center gap-2">
            <LanguageSelector />
            <button
              onClick={() => navigate('/login')}
              className="hidden sm:block px-3 py-2 text-sm font-medium text-gray-700 hover:text-orange-600 transition-colors"
            >
              {t('nav.signIn')}
            </button>
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 rounded-lg hover:bg-orange-50 transition-colors"
              data-testid="mobile-menu-button"
            >
              {mobileMenuOpen ? <X className="w-6 h-6 text-gray-700" /> : <Menu className="w-6 h-6 text-gray-700" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden fixed top-20 left-0 right-0 border-t border-orange-100 bg-white z-40 max-h-[calc(100vh-5rem)] overflow-y-auto" data-testid="mobile-menu">
            <nav className="px-4 py-4 space-y-3">
              {/* Language Selector for Mobile - Hidden if already shown in header */}
              <div className="px-4 py-2 flex items-center justify-between border-b border-gray-100 pb-3 sm:hidden">
                <span className="text-sm text-gray-600">Language</span>
                <LanguageSelector />
              </div>
              
              <button
                onClick={() => { navigate('/treatments'); setMobileMenuOpen(false); }}
                className="w-full text-left px-4 py-3 text-gray-700 hover:bg-orange-50 hover:text-orange-600 rounded-lg transition-colors flex items-center gap-3"
              >
                <Stethoscope className="w-5 h-5 text-orange-500" />
                {t('nav.treatments')}
              </button>
              <button
                onClick={() => { navigate('/doctors'); setMobileMenuOpen(false); }}
                className="w-full text-left px-4 py-3 text-gray-700 hover:bg-orange-50 hover:text-orange-600 rounded-lg transition-colors flex items-center gap-3"
              >
                <UserRound className="w-5 h-5 text-orange-500" />
                {t('nav.doctors')}
              </button>
              <button
                onClick={() => { navigate('/hospitals'); setMobileMenuOpen(false); }}
                className="w-full text-left px-4 py-3 text-gray-700 hover:bg-orange-50 hover:text-orange-600 rounded-lg transition-colors flex items-center gap-3"
              >
                <Building2 className="w-5 h-5 text-orange-500" />
                {t('nav.hospitals')}
              </button>
              
              {/* Services Section in Mobile */}
              <div className="px-4 py-2">
                <p className="text-sm font-semibold text-orange-600 mb-2 flex items-center gap-2">
                  <Briefcase className="w-4 h-4" />
                  {t('nav.services')}
                </p>
                <div className="space-y-1 pl-2">
                  {services.map((service) => {
                    const ServiceIcon = service.icon;
                    return (
                      <button
                        key={service.path}
                        onClick={() => { navigate(service.path); setMobileMenuOpen(false); }}
                        className="w-full text-left px-3 py-2 text-sm text-gray-600 hover:bg-orange-50 hover:text-orange-600 rounded-lg transition-colors flex items-center gap-2"
                        data-testid={`mobile-service-link-${service.path.split('/').pop()}`}
                      >
                        <ServiceIcon className="w-4 h-4 text-orange-400" />
                        {service.name}
                      </button>
                    );
                  })}
                </div>
              </div>
              
              <button
                onClick={() => { navigate('/wellness'); setMobileMenuOpen(false); }}
                className="w-full text-left px-4 py-3 text-gray-700 hover:bg-orange-50 hover:text-orange-600 rounded-lg transition-colors flex items-center gap-3"
              >
                <Sparkles className="w-5 h-5 text-orange-500" />
                {t('nav.wellness')}
              </button>
              <button
                onClick={() => { navigate('/medical-visa'); setMobileMenuOpen(false); }}
                className="w-full text-left px-4 py-3 text-gray-700 hover:bg-orange-50 hover:text-orange-600 rounded-lg transition-colors flex items-center gap-3"
              >
                <FileText className="w-5 h-5 text-orange-500" />
                {t('nav.medicalVisa')}
              </button>
              <div className="pt-3 border-t border-gray-200 space-y-2">
                <button
                  onClick={() => { navigate('/login'); setMobileMenuOpen(false); }}
                  className="block w-full text-center px-4 py-3 text-gray-700 hover:bg-orange-50 hover:text-orange-600 rounded-lg transition-colors font-medium"
                >
                  {t('nav.signIn')}
                </button>
                <button
                  onClick={() => { navigate('/register'); setMobileMenuOpen(false); }}
                  className="block w-full text-center px-4 py-3 bg-gradient-to-r from-orange-500 to-red-600 text-white rounded-lg font-semibold hover:shadow-lg transition-all"
                >
                  {t('nav.getStarted')}
                </button>
              </div>
            </nav>
          </div>
        )}
      </header>
);
